'use client';

import { useCallback, useState } from 'react';
import { useRouter } from 'next/navigation';

import { useAppEvents } from '@kit/shared/events';
import { useSignUpWithEmailAndPassword } from '@kit/supabase/hooks/use-sign-up-with-email-password';

import {
  checkSignupIdentityDuplicate,
  makeExistingAccountError,
} from '../lib/signup-identity-guard';
import { resolvePostSignupPath } from './resolve-post-signup-path';
import { useLastAuthMethod } from './use-last-auth-method';

type SignUpCredentials = {
  email: string;
  password: string;
};

type UseSignUpFlowProps = {
  emailRedirectTo: string;
  onSignUp?: (userId?: string) => unknown;
  captchaToken?: string;
  resetCaptchaToken?: () => void;
  /**
   * Optional metadata to attach to the user at signup time (e.g.
   * `{ signup_brand: 'flow' | 'ballee' }`). Read back later from
   * `user.user_metadata.signup_brand` to inform brand-aware analytics
   * and onboarding flows.
   */
  signupMetadata?: Record<string, unknown>;
};

/**
 * @name usePasswordSignUpFlow
 * @description
 * This hook is used to handle the sign up flow using the email and password method.
 */
export function usePasswordSignUpFlow({
  emailRedirectTo,
  onSignUp,
  captchaToken,
  resetCaptchaToken,
  signupMetadata,
}: UseSignUpFlowProps) {
  const router = useRouter();
  const signUpMutation = useSignUpWithEmailAndPassword();
  const appEvents = useAppEvents();
  const { recordAuthMethod } = useLastAuthMethod();
  const [requiresEmailConfirmation, setRequiresEmailConfirmation] =
    useState(false);
  // Set when the pre-signup identity guard blocks a canonical-duplicate
  // registration (BAL-718). Shaped as an "account exists" error so the
  // container's existing recovery UX (isAccountExistsError → AuthRecoveryHint)
  // routes the user to sign-in instead of minting a duplicate profile.
  const [identityError, setIdentityError] = useState<Error | null>(null);

  const signUp = useCallback(
    async (credentials: SignUpCredentials) => {
      if (signUpMutation.isPending) {
        return;
      }

      // Reset any prior guard verdict before re-checking this attempt.
      setIdentityError(null);

      // Funnel entry: emitted before the identity guard and the GoTrue call so
      // every attempt is counted, including the ones that never reach
      // `user.signedUp`.
      appEvents.emit({
        type: 'user.signUpStarted',
        payload: {
          method: 'password',
        },
      });

      // Pre-signup duplicate-identity guard: if an account already exists for
      // the CANONICAL form of this email (gmail dot/plus, RFC subaddress),
      // block here — creating a new auth user would mint a duplicate profile.
      // Fails open (returns false) on any lookup error, so infra trouble never
      // blocks a legitimate signup; GoTrue still rejects exact duplicates.
      const isDuplicateIdentity = await checkSignupIdentityDuplicate(
        credentials.email,
      );

      if (isDuplicateIdentity) {
        setIdentityError(makeExistingAccountError());
        return;
      }

      try {
        const data = await signUpMutation.mutateAsync({
          ...credentials,
          emailRedirectTo,
          captchaToken,
          ...(signupMetadata ? { data: signupMetadata } : {}),
        });

        // Record last auth method
        recordAuthMethod('password', { email: credentials.email });

        // emit event to track sign up
        appEvents.emit({
          type: 'user.signedUp',
          payload: {
            method: 'password',
          },
        });

        // Check if we have a session (email confirmations disabled)
        if (data.session) {
          // User is immediately logged in. Mirror /auth/callback's apply-
          // journey wrap so dancer signups arriving from an audition CTA
          // still land in the profile-setup wizard with `?next=` preserved
          // — without this, the wizard's auto-enroll branch never fires.
          if (onSignUp) {
            onSignUp(data.user?.id);
          }
          router.push(resolvePostSignupPath());
          return;
        }

        // Only proceed with confirmation flow if no session was created
        // Update URL with success status. This is useful for password managers
        // to understand that the form was submitted successfully.
        const url = new URL(window.location.href);

        url.searchParams.set('status', 'success');
        router.replace(url.pathname + url.search);

        // Show email confirmation alert
        setRequiresEmailConfirmation(true);

        if (onSignUp) {
          onSignUp(data.user?.id);
        }
      } catch (error: unknown) {
        console.error(error);

        throw error;
      } finally {
        resetCaptchaToken?.();
      }
    },
    [
      signUpMutation,
      emailRedirectTo,
      captchaToken,
      signupMetadata,
      appEvents,
      router,
      onSignUp,
      resetCaptchaToken,
      recordAuthMethod,
      setRequiresEmailConfirmation,
    ],
  );

  return {
    signUp,
    loading: signUpMutation.isPending,
    // Surface the guard's "account exists" verdict through the same `error`
    // channel the container already renders, so no new UI/i18n is needed.
    error: identityError ?? signUpMutation.error,
    showVerifyEmailAlert: requiresEmailConfirmation,
  };
}
